import { Button } from "@/components/ui/button";
import ColorIconChooser from "@/components/ui/color-icon-chooser";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { getWorkspaceIconComponent } from "@/lib/constants/workspace-customization";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { updateUserWorkspaces } from "@/lib/utils/notificationUtils";
import { chatCompletionService } from "@/services/openai/chatCompletionService";
import useAuthStore from "@/store/authStore";
import { Lightbulb, User, Users, X } from "lucide-react";
import { useState } from "react";

export default function WorkspaceCreateDialog({
  isOpen,
  onOpenChange,
  onCreateWorkspace,
  isLoading,
}) {
  const user = useAuthStore((state) => state.user);
  const [newWorkspace, setNewWorkspace] = useState({
    name: "",
    description: "",
    instructions: "",
    is_personal: false,
    is_team: false,
    team_key: "",
    allow_invites: true,
    logo: null,
    members: [],
    created_now: true,
  });

  const [step, setStep] = useState(1);
  const [workspaceType, setWorkspaceType] = useState(null);
  const [isColorIconChooserOpen, setIsColorIconChooserOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#f8f9fa");
  const [selectedIcon, setSelectedIcon] = useState("folder");

  const handleWorkspaceTypeChange = (value) => {
    const isTeam = value === "team";
    setWorkspaceType(value);

    setNewWorkspace({
      ...newWorkspace,
      is_team: isTeam,
      is_personal: !isTeam,
      // Generate team key if switching to team workspace
      team_key: isTeam
        ? `team_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 10)}`
        : "",
    });
  };

  const handleSubmit = async () => {
    const workspaceData = {
      ...newWorkspace,
      // Add the creator to the members array
      members: user?.id ? [user.id] : [],
      // CRITICAL: Ensure created_now flag is set for team workspaces
      created_now: newWorkspace.is_team,
      // Also set in metadata for additional reliability
      metadata: {
        ...(newWorkspace.metadata || {}),
        created_now: newWorkspace.is_team,
        workspace_type: newWorkspace.is_team ? "team" : "personal",
        created_at: new Date().toISOString(),
        // Include icon and color for all projects
        color: selectedColor,
        icon: selectedIcon,
      },
    };

    // Log the final data being submitted

    // Create the workspace
    const createdWorkspace = await onCreateWorkspace(workspaceData);

    // If workspace creation was successful, create a vector store for the workspace
    if (createdWorkspace && createdWorkspace.id && user?.id) {
      try {
        // Add the creator to the workspace_members array in the database
        await supabase
          .from("workspaces")
          .update({
            workspace_members: [user.id],
          })
          .eq("id", createdWorkspace.id);

        // Create a vector store using ChatCompletionService
        const vectorStore = await chatCompletionService.createVectorStore(
          createdWorkspace.name || workspaceData.name
        );

        // Update the workspace with the vector store ID
        // IMPORTANT: Preserve the created_now flag and metadata when updating
        const { data: workspaceWithVectorStore, error: updateError } =
          await supabase
            .from("workspaces")
            .update({
              vector_store_id: vectorStore.id,
              metadata: {
                ...(createdWorkspace.metadata || {}),
                vector_store_id: vectorStore.id,
                vector_store_created_at: new Date().toISOString(),
                vector_store_provider: "openai",
                // Preserve the created_now flag
                created_now: workspaceData.is_team,
                workspace_type: workspaceData.is_team ? "team" : "personal",
              },
            })
            .eq("id", createdWorkspace.id)
            .select()
            .single();

        if (updateError) {
          console.error(
            "Error updating workspace with vector store:",
            updateError
          );
        }

        // Add the workspace ID to the user's profile user_workspaces array
        await updateUserWorkspaces(user.id, createdWorkspace.id);

        // CRITICAL FIX: Update the auth store with the vector store ID
        const { setCurrentWorkspace, workspaces, setWorkspaces } =
          useAuthStore.getState();

        // Create the updated workspace object with vector store ID
        const updatedWorkspace = workspaceWithVectorStore || {
          ...createdWorkspace,
          vector_store_id: vectorStore.id,
          metadata: {
            ...(createdWorkspace.metadata || {}),
            vector_store_id: vectorStore.id,
            vector_store_created_at: new Date().toISOString(),
            vector_store_provider: "openai",
            created_now: workspaceData.is_team,
            workspace_type: workspaceData.is_team ? "team" : "personal",
          },
        };

        // Update current workspace if this is the active one
        if (
          useAuthStore.getState().currentWorkspaceId === createdWorkspace.id
        ) {
          setCurrentWorkspace(updatedWorkspace);
        }

        // Update the workspaces list in auth store
        const currentWorkspaces = workspaces || [];
        const workspaceIndex = currentWorkspaces.findIndex(
          (w) => w.id === createdWorkspace.id
        );

        if (workspaceIndex >= 0) {
          // Update existing workspace
          const newWorkspaces = [...currentWorkspaces];
          newWorkspaces[workspaceIndex] = updatedWorkspace;
          setWorkspaces(newWorkspaces);
        } else {
          // Add new workspace
          setWorkspaces([...currentWorkspaces, updatedWorkspace]);
        }

        // Store the vector store ID in localStorage as a fallback
        localStorage.setItem(
          `workspace_${createdWorkspace.id}_vector_store_id`,
          vectorStore.id
        );
      } catch (error) {
        console.error("Error creating vector store:", error);
        // Continue despite error - we don't want to prevent workspace creation if vector store creation fails
      }
    }

    // Reset form after submission
    setNewWorkspace({
      name: "",
      description: "",
      instructions: "",
      is_personal: false,
      is_team: false,
      team_key: "",
      allow_invites: true,
      logo: null,
      image_path: null,
      members: [],
      created_now: true,
    });
    setStep(1);
    setWorkspaceType(null);
    setSelectedColor("#f8f9fa");
    setSelectedIcon("folder");

    // Clear any stale localStorage items that might cause automatic navigation
    localStorage.removeItem("pendingWorkspaceMessage");
    localStorage.removeItem("pendingWorkspaceChat");
    localStorage.removeItem("pendingWorkspaceType");
    localStorage.removeItem("pendingWorkspaceFiles");
    localStorage.removeItem("pendingVisionImageUrls");
    localStorage.removeItem("pendingFileMetadata");
    localStorage.removeItem("pendingThreadTitle");
    localStorage.removeItem("createSupabaseOnlyThread");
    localStorage.removeItem("forceNewThread");
    localStorage.removeItem("clearMessagesAndContext");
    localStorage.removeItem("preserveUserMessages");
    localStorage.removeItem("needs_title_generation");
    localStorage.removeItem("pending_thread_title");
  };

  const resetDialog = () => {
    setNewWorkspace({
      name: "",
      description: "",
      instructions: "",
      is_personal: false,
      is_team: false,
      team_key: "",
      allow_invites: true,
      logo: null,
      members: [],
      created_now: true,
    });
    setStep(1);
    setWorkspaceType(null);
    setSelectedColor("#f8f9fa");
    setSelectedIcon("folder");

    // Clear any stale localStorage items that might cause automatic navigation
    localStorage.removeItem("pendingWorkspaceMessage");
    localStorage.removeItem("pendingWorkspaceChat");
    localStorage.removeItem("pendingWorkspaceType");
    localStorage.removeItem("pendingWorkspaceFiles");
    localStorage.removeItem("pendingVisionImageUrls");
    localStorage.removeItem("pendingFileMetadata");
    localStorage.removeItem("pendingThreadTitle");
    localStorage.removeItem("createSupabaseOnlyThread");
    localStorage.removeItem("forceNewThread");
    localStorage.removeItem("clearMessagesAndContext");
    localStorage.removeItem("preserveUserMessages");
    localStorage.removeItem("needs_title_generation");
    localStorage.removeItem("pending_thread_title");
  };

  const handleDialogOpenChange = (open) => {
    if (!open) {
      resetDialog();
    }
    onOpenChange(open);
  };

  const isNextDisabled = () => {
    if (step === 1) return !workspaceType;
    return false;
  };

  const moveToNextStep = () => {
    setStep((current) => Math.min(current + 1, 2));
  };

  const moveToPreviousStep = () => {
    setStep((current) => Math.max(current - 1, 1));
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="max-w-[90vw] max-h-[90vh] w-[480px] bg-zinc-900 border-zinc-700/50 text-white p-0 gap-0 rounded-2xl flex flex-col overflow-hidden"
        hideCloseButton={true}
        style={{ zIndex: 9999 }}
      >
        <DialogTitle className="sr-only">Create New Project</DialogTitle>
        <DialogDescription className="sr-only">
          Create a new workspace or team project
        </DialogDescription>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-900 flex-shrink-0 rounded-t-2xl">
          <h2 className="text-base font-semibold text-white">
            {step === 1 ? "Create New Project" : "Project name"}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            className="h-7 w-7 bg-transparent hover:bg-zinc-800 text-zinc-400 hover:text-white border-none"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden bg-zinc-900 rounded-b-2xl">
          <div className="flex-1 overflow-auto">
            <div className="p-4">
              {/* Step 1: Choose project type */}
              {step === 1 && (
                <div className="grid gap-3 grid-cols-2">
                  <div
                    className={cn(
                      "flex flex-col items-center justify-center py-6 px-4 rounded-xl border transition-all cursor-pointer",
                      workspaceType === "personal"
                        ? "border-white/80 bg-zinc-800/50"
                        : "border-zinc-700/50 bg-zinc-800/30 hover:border-zinc-600"
                    )}
                    onClick={() => handleWorkspaceTypeChange("personal")}
                  >
                    <div className="h-10 w-10 rounded-full bg-zinc-700 flex items-center justify-center mb-3">
                      <User className="h-5 w-5 text-zinc-300" />
                    </div>
                    <h3 className="font-medium text-sm text-white">
                      Personal Project
                    </h3>
                  </div>

                  <div
                    className={cn(
                      "flex flex-col items-center justify-center py-6 px-4 rounded-xl border transition-all cursor-pointer",
                      workspaceType === "team"
                        ? "border-white/80 bg-zinc-800/50"
                        : "border-zinc-700/50 bg-zinc-800/30 hover:border-zinc-600"
                    )}
                    onClick={() => handleWorkspaceTypeChange("team")}
                  >
                    <div className="h-10 w-10 rounded-full bg-zinc-700 flex items-center justify-center mb-3">
                      <Users className="h-5 w-5 text-zinc-300" />
                    </div>
                    <h3 className="font-medium text-sm text-white">
                      Team Project
                    </h3>
                  </div>
                </div>
              )}

              {/* Step 2: Project name */}
              {step === 2 && (
                <div className="grid gap-4">
                  {/* Name input with icon prefix */}
                  <div className="flex items-center gap-3">
                    {/* Icon - ColorIconChooser for both team and personal projects */}
                    <ColorIconChooser
                      isOpen={isColorIconChooserOpen}
                      onOpenChange={setIsColorIconChooserOpen}
                      currentColor={selectedColor}
                      currentIcon={selectedIcon}
                      onColorSelect={(color) => setSelectedColor(color.value)}
                      onIconSelect={(icon) => setSelectedIcon(icon.value)}
                    >
                      <button
                        type="button"
                        onClick={() => setIsColorIconChooserOpen(true)}
                        className={cn(
                          "h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-lg",
                          "border border-zinc-700",
                          "bg-zinc-800",
                          "hover:bg-zinc-700",
                          "transition-colors cursor-pointer"
                        )}
                      >
                        {(() => {
                          const IconComponent = getWorkspaceIconComponent(selectedIcon);
                          return (
                            <IconComponent
                              className="h-5 w-5"
                              style={{ color: selectedColor === "#f8f9fa" ? "#d4d4d8" : selectedColor }}
                            />
                          );
                        })()}
                      </button>
                    </ColorIconChooser>

                    {/* Name Input */}
                    <Input
                      id="name"
                      value={newWorkspace.name}
                      onChange={(e) =>
                        setNewWorkspace({
                          ...newWorkspace,
                          name: e.target.value,
                        })
                      }
                      placeholder="E.g. Birthday Party Planning"
                      className="flex-1 border-zinc-700 bg-zinc-800 text-zinc-100 placeholder:text-zinc-500 h-10"
                    />
                  </div>

                  {/* Helper text box */}
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
                    <div className="h-5 w-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <Lightbulb className="h-4 w-4 text-zinc-400" />
                    </div>
                    <div className="text-sm text-zinc-400">
                      <p className="font-medium text-zinc-300 mb-1">
                        {newWorkspace.is_team
                          ? "Team project customization"
                          : "Personal project customization"}
                      </p>
                      <p>
                        {newWorkspace.is_team
                          ? "Click the icon to customize the look of your personal project. Projects keep chats, files, and custom instructions organized."
                          : "Click the icon to choose a color and symbol for your personal project. Projects keep chats, files, and custom instructions organized."}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end px-4 py-3 border-t border-zinc-800 bg-zinc-900 flex-shrink-0">
            {step > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={moveToPreviousStep}
                className="mr-auto text-zinc-300 border-zinc-600 hover:text-white hover:border-white rounded-full px-5 h-9 text-sm"
              >
                Back
              </Button>
            )}

            {step < 2 ? (
              <Button
                type="button"
                onClick={moveToNextStep}
                disabled={isNextDisabled()}
                className="bg-white hover:bg-zinc-200 text-zinc-900 rounded-full px-5 h-9 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isLoading || !newWorkspace.name}
                className="bg-white hover:bg-zinc-200 text-zinc-900 rounded-full px-5 h-9 text-sm"
              >
                {isLoading ? "Creating..." : "Create Project"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
